import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Settings, Activity, Brain, Shield, Save, RefreshCw, Clock, AlertCircle, CalendarDays, DoorOpen, Users, Key, CheckCircle2, XCircle, Loader2, Syringe } from 'lucide-react';
import ClinicalProceduresCatalog from './ClinicalProceduresCatalog';
import { toast } from 'sonner';
import ScheduleTemplateEditor from '../components/schedule/ScheduleTemplateEditor';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('visit_types');
  const [formState, setFormState] = useState({});
  const [visitTypes, setVisitTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomForm, setRoomForm] = useState({ name: '', room_type: 'exam_room', is_shared: false, assigned_provider_ids: [] });
  const [nurseMaps, setNurseMaps] = useState([]);
  const [nurseUsers, setNurseUsers] = useState([]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settRes, vtRes, provRes, roomRes] = await Promise.all([
        api.get('/settings'),
        api.get('/visit-types'),
        api.get('/providers'),
        api.get('/rooms?active_only=false').catch(() => ({ data: { rooms: [] } })),
      ]);
      setSettings(settRes.data.settings);
      setFormState(settRes.data.settings || {});
      setVisitTypes(vtRes.data.visit_types || []);
      setProviders(provRes.data.providers || []);
      setRooms(roomRes.data.rooms || []);
      // Nurse team maps
      try {
        const [nmRes, nuRes] = await Promise.all([
          api.get('/provider-nurse-maps'),
          api.get('/nurse-users'),
        ]);
        setNurseMaps(nmRes.data.maps || []);
        setNurseUsers(nuRes.data.nurses || []);
      } catch {}
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateField = (section, key, value) => {
    setFormState(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value },
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { id, clinic_id, ...updates } = formState;
      await api.put('/settings', updates);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="p-6 max-w-4xl space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  const dx = formState.diagnosis_lifecycle || {};
  const ai = formState.ai_defaults || {};
  const cc = formState.command_center || {};
  const sd = formState.schedule_display || {};

  const updateVisitType = async (vtId, updates) => {
    try {
      await api.put(`/visit-types/${vtId}`, updates);
      toast.success('Visit type updated');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="animate-fadeIn" data-testid="settings-page">
      <div className="p-4 sm:p-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Settings className="w-5 h-5 text-primary" /> Clinic Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Configure diagnosis rules, AI behavior, and Command Center thresholds</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSettings}>
              <RefreshCw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="settings-save-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="visit_types" className="text-xs gap-1">
              <Clock className="w-3 h-3" /> Visit Types
            </TabsTrigger>
            <TabsTrigger value="schedules" className="text-xs gap-1">
              <CalendarDays className="w-3 h-3" /> Provider Schedules
            </TabsTrigger>
            <TabsTrigger value="schedule_display" className="text-xs gap-1">
              <Settings className="w-3 h-3" /> Schedule Display
            </TabsTrigger>
            <TabsTrigger value="diagnosis" className="text-xs gap-1">
              <Activity className="w-3 h-3" /> Diagnosis Lifecycle
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs gap-1">
              <Brain className="w-3 h-3" /> AI Configuration
            </TabsTrigger>
            <TabsTrigger value="command_center" className="text-xs gap-1">
              <Shield className="w-3 h-3" /> Command Center
            </TabsTrigger>
            <TabsTrigger value="rooms" className="text-xs gap-1">
              <DoorOpen className="w-3 h-3" /> Rooms
            </TabsTrigger>
            <TabsTrigger value="nurse_teams" className="text-xs gap-1">
              <Users className="w-3 h-3" /> Nurse Teams
            </TabsTrigger>
            <TabsTrigger value="procedures" className="text-xs gap-1">
              <Syringe className="w-3 h-3" /> Procedures
            </TabsTrigger>
          </TabsList>

          {/* Visit Types Configuration */}
          <TabsContent value="visit_types" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Appointment Visit Types</CardTitle>
                <CardDescription className="text-xs">Configure visit types, durations, and booking rules. Durations are fixed at 15, 30, 45, or 60 minutes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {visitTypes.map(vt => (
                  <div key={vt.id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`visit-type-${vt.name.replace(/\s/g, '-').toLowerCase()}`}>
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: vt.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{vt.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px]">{vt.duration_minutes} min</Badge>
                        {vt.allow_double_book && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Double-bookable</Badge>}
                        {vt.max_per_day > 0 && <Badge variant="outline" className="text-[10px]">Max {vt.max_per_day}/day</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select className="h-7 text-xs rounded border bg-background px-2"
                        value={vt.duration_minutes}
                        onChange={e => updateVisitType(vt.id, { duration_minutes: parseInt(e.target.value) })}>
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={45}>45 min</option>
                        <option value={60}>60 min</option>
                      </select>
                      <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer">
                        <input type="checkbox" className="rounded" checked={vt.allow_double_book || false}
                          onChange={e => updateVisitType(vt.id, { allow_double_book: e.target.checked })} />
                        2x Book
                      </label>
                      <Input type="number" className="h-7 w-16 text-xs" placeholder="Max/day" value={vt.max_per_day || 0}
                        onChange={e => updateVisitType(vt.id, { max_per_day: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Provider Overview</CardTitle>
                <CardDescription className="text-xs">Active providers in the system.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {providers.map(prov => (
                  <div key={prov.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" data-testid={`provider-overview-${prov.name.replace(/\s/g, '-').toLowerCase()}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: prov.color }} />
                    <span className="text-sm font-medium">{prov.name}</span>
                    <span className="text-xs text-muted-foreground">{prov.specialty}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Provider Schedule Templates - Visual Editor */}
          <TabsContent value="schedules" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Schedule Template Editor</CardTitle>
                <CardDescription className="text-xs">
                  Visually manage provider weekly schedules. Configure available hours, lunch breaks, admin time, and meetings.
                  Changes are admin-controlled and affect booking availability.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScheduleTemplateEditor providers={providers} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Display Settings */}
          <TabsContent value="schedule_display" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Schedule Grid Display</CardTitle>
                <CardDescription className="text-xs">Control how the schedule grid appears. Changes apply to all users viewing the schedule.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label className="text-xs font-medium">Grid Interval</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5 mb-3">
                    Choose how time slots are divided on the schedule board. This controls which rows are visible.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 15, label: '15 Minutes', desc: 'Every quarter hour — shows all available booking slots' },
                      { value: 30, label: '30 Minutes', desc: 'Half-hour intervals — balanced view with less density' },
                      { value: 60, label: '1 Hour', desc: 'Hourly blocks — most compact, high-level overview' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          (sd.grid_interval || 15) === opt.value
                            ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20'
                            : 'bg-background border-border hover:bg-accent/50'
                        }`}
                        onClick={() => updateField('schedule_display', 'grid_interval', opt.value)}
                        data-testid={`grid-interval-${opt.value}`}
                      >
                        <span className={`text-sm font-semibold ${(sd.grid_interval || 15) === opt.value ? 'text-primary' : ''}`}>
                          {opt.label}
                        </span>
                        <p className="text-[10px] text-muted-foreground mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="rounded-lg border bg-muted/20 p-3">
                  <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wide font-medium">Preview</p>
                  <div className="space-y-0">
                    {[0, 1, 2, 3].map(i => {
                      const interval = sd.grid_interval || 15;
                      const totalMin = 540 + (i * interval); // 540 = 9:00 AM in minutes
                      const h = Math.floor(totalMin / 60);
                      const m = totalMin % 60;
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      const timeLabel = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                      const isHour = m === 0;
                      const isHalf = m === 30;
                      return (
                        <div key={i} className={`flex items-center gap-2 py-1.5 ${
                          isHour ? 'border-t-2 border-slate-300' : isHalf ? 'border-t border-slate-200' : 'border-t border-dashed border-slate-100'
                        }`}>
                          <span className="text-[10px] tabular-nums text-muted-foreground w-16">
                            {timeLabel}
                          </span>
                          <div className="flex-1 h-4 rounded bg-background border border-border/50" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Diagnosis Lifecycle */}
          <TabsContent value="diagnosis" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Acute Conditions</CardTitle>
                <CardDescription className="text-xs">Rules for acute diagnosis aging and review</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Expiration Review (days)</Label>
                    <Input type="number" value={dx.acute_expiration_review_days || 90}
                      onChange={e => updateField('diagnosis_lifecycle', 'acute_expiration_review_days', parseInt(e.target.value) || 90)}
                      className="mt-1" data-testid="settings-acute-expiration" />
                    <p className="text-[10px] text-muted-foreground mt-1">Days before an acute diagnosis is flagged for review</p>
                  </div>
                  <div>
                    <Label className="text-xs">Promotion Criteria (days)</Label>
                    <Input type="number" value={dx.acute_to_chronic_min_days || 180}
                      onChange={e => updateField('diagnosis_lifecycle', 'acute_to_chronic_min_days', parseInt(e.target.value) || 180)}
                      className="mt-1" data-testid="settings-acute-chronic-days" />
                    <p className="text-[10px] text-muted-foreground mt-1">Minimum days before suggesting acute→chronic promotion</p>
                  </div>
                  <div>
                    <Label className="text-xs">Promotion Encounters (minimum)</Label>
                    <Input type="number" value={dx.acute_to_chronic_min_encounters || 3}
                      onChange={e => updateField('diagnosis_lifecycle', 'acute_to_chronic_min_encounters', parseInt(e.target.value) || 3)}
                      className="mt-1" data-testid="settings-acute-chronic-encounters" />
                    <p className="text-[10px] text-muted-foreground mt-1">Minimum encounter references before suggesting promotion</p>
                  </div>
                  <div>
                    <Label className="text-xs">Promotion Behavior</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={`text-xs cursor-pointer transition-colors ${dx.promotion_behavior === 'suggest' ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => updateField('diagnosis_lifecycle', 'promotion_behavior', 'suggest')}>
                        Suggest
                      </Badge>
                      <Badge variant="outline" className={`text-xs cursor-pointer transition-colors ${dx.promotion_behavior === 'auto_promote' ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => updateField('diagnosis_lifecycle', 'promotion_behavior', 'auto_promote')}>
                        Auto-promote
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Whether to suggest or automatically promote</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Chronic Conditions</CardTitle>
                <CardDescription className="text-xs">Rules for chronic diagnosis inactivity management</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Inactivity Review (days)</Label>
                    <Input type="number" value={dx.chronic_inactivity_review_days || 365}
                      onChange={e => updateField('diagnosis_lifecycle', 'chronic_inactivity_review_days', parseInt(e.target.value) || 365)}
                      className="mt-1" data-testid="settings-chronic-inactivity" />
                    <p className="text-[10px] text-muted-foreground mt-1">Days of inactivity before flagging chronic diagnosis for review</p>
                  </div>
                  <div>
                    <Label className="text-xs">Inactivity Behavior</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={`text-xs cursor-pointer transition-colors ${dx.inactivity_behavior === 'suggest_review' ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => updateField('diagnosis_lifecycle', 'inactivity_behavior', 'suggest_review')}>
                        Suggest Review
                      </Badge>
                      <Badge variant="outline" className={`text-xs cursor-pointer transition-colors ${dx.inactivity_behavior === 'auto_inactive' ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => updateField('diagnosis_lifecycle', 'inactivity_behavior', 'auto_inactive')}>
                        Auto-mark Inactive
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">What happens when chronic condition is inactive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Configuration */}
          <TabsContent value="ai" className="space-y-4 m-0">
            <AiCredentialsCard />
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">AI Pipeline Settings</CardTitle>
                <CardDescription className="text-xs">Configure AI behavior and fallback modes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">Default AI Mode</Label>
                  <div className="flex items-center gap-2 mt-2">
                    {['full_ai', 'local_only', 'manual_only'].map(mode => (
                      <Badge key={mode} variant="outline"
                        className={`text-xs cursor-pointer transition-colors ${ai.default_mode === mode ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => updateField('ai_defaults', 'default_mode', mode)}>
                        {mode.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">full_ai: cloud LLM | local_only: local model (future) | manual_only: no AI</p>
                </div>
                <div>
                  <Label className="text-xs">Confidence Threshold</Label>
                  <Input type="number" step="0.05" min="0" max="1" value={ai.confidence_threshold || 0.5}
                    onChange={e => updateField('ai_defaults', 'confidence_threshold', parseFloat(e.target.value) || 0.5)}
                    className="mt-1 max-w-[200px]" data-testid="settings-ai-confidence" />
                  <p className="text-[10px] text-muted-foreground mt-1">Items below this threshold are flagged as low-confidence</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Command Center */}
          <TabsContent value="command_center" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Command Center Behavior</CardTitle>
                <CardDescription className="text-xs">Control how items are displayed and batched in the Command Center</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Low Confidence Threshold</Label>
                    <Input type="number" step="0.05" min="0" max="1" value={cc.low_confidence_threshold || 0.5}
                      onChange={e => updateField('command_center', 'low_confidence_threshold', parseFloat(e.target.value) || 0.5)}
                      className="mt-1" data-testid="settings-cc-low-confidence" />
                    <p className="text-[10px] text-muted-foreground mt-1">Items below this are grouped in a low-confidence drawer</p>
                  </div>
                  <div>
                    <Label className="text-xs">High Confidence Threshold</Label>
                    <Input type="number" step="0.05" min="0" max="1" value={cc.high_confidence_threshold || 0.85}
                      onChange={e => updateField('command_center', 'high_confidence_threshold', parseFloat(e.target.value) || 0.85)}
                      className="mt-1" data-testid="settings-cc-high-confidence" />
                    <p className="text-[10px] text-muted-foreground mt-1">Items above this are considered high-confidence</p>
                  </div>
                  <div>
                    <Label className="text-xs">Max Items Per Section</Label>
                    <Input type="number" value={cc.max_items_per_section || 10}
                      onChange={e => updateField('command_center', 'max_items_per_section', parseInt(e.target.value) || 10)}
                      className="mt-1" data-testid="settings-cc-max-items" />
                  </div>
                  <div>
                    <Label className="text-xs">Collapse Threshold</Label>
                    <Input type="number" value={cc.collapse_threshold || 5}
                      onChange={e => updateField('command_center', 'collapse_threshold', parseInt(e.target.value) || 5)}
                      className="mt-1" data-testid="settings-cc-collapse" />
                    <p className="text-[10px] text-muted-foreground mt-1">Auto-collapse approved items when total exceeds this number</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rooms Configuration */}
          <TabsContent value="rooms" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Clinic Rooms</CardTitle>
                <CardDescription className="text-xs">Define exam rooms, procedure rooms, and assign to providers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add Room Form */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Room Name</Label>
                    <Input value={roomForm.name} onChange={e => setRoomForm(f => ({...f, name: e.target.value}))}
                      placeholder="e.g., Room 1, Procedure Room, Telemed" className="text-sm mt-1" data-testid="room-name-input" />
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Type</Label>
                    <select value={roomForm.room_type} onChange={e => setRoomForm(f => ({...f, room_type: e.target.value}))}
                      className="w-full h-9 mt-1 text-sm border rounded-md px-2" data-testid="room-type-select">
                      <option value="exam_room">Exam Room</option>
                      <option value="procedure_room">Procedure Room</option>
                      <option value="telemed_room">Telemed Room</option>
                      <option value="imaging_room">Imaging / X-Ray</option>
                      <option value="lab_room">Lab / Nurse Room</option>
                      <option value="shared_room">Shared / Overflow</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pb-1">
                    <Switch checked={roomForm.is_shared} onCheckedChange={v => setRoomForm(f => ({...f, is_shared: v}))} data-testid="room-shared-toggle" />
                    <Label className="text-xs">Shared</Label>
                  </div>
                  <Button size="sm" className="h-9" data-testid="room-add-btn" onClick={async () => {
                    if (!roomForm.name.trim()) { toast.error('Room name required'); return; }
                    try {
                      await api.post('/rooms', { name: roomForm.name, room_type: roomForm.room_type, is_shared: roomForm.is_shared, assigned_provider_ids: roomForm.assigned_provider_ids, display_order: rooms.length + 1 });
                      toast.success(`Room "${roomForm.name}" created`);
                      setRoomForm({ name: '', room_type: 'exam_room', is_shared: false, assigned_provider_ids: [] });
                      fetchSettings();
                    } catch { toast.error('Failed to create room'); }
                  }}>Add Room</Button>
                </div>

                {/* Room List */}
                {rooms.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No rooms configured yet. Add your first room above.</p>
                ) : (
                  <div className="space-y-2">
                    {rooms.map(room => (
                      <div key={room.id} className={`flex items-center gap-3 p-3 rounded-lg border ${room.is_active ? 'bg-card' : 'bg-muted/30 opacity-60'}`} data-testid={`room-${room.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{room.name}</span>
                            <Badge variant="outline" className="text-[9px]">{room.room_type?.replace(/_/g, ' ')}</Badge>
                            {room.is_shared && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600">Shared</Badge>}
                            {!room.is_active && <Badge variant="outline" className="text-[9px] bg-red-50 text-red-500">Inactive</Badge>}
                          </div>
                          {/* Provider assignments */}
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {providers.map(prov => {
                              const assigned = (room.assigned_provider_ids || []).includes(prov.id);
                              return (
                                <button key={prov.id}
                                  className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${assigned ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted/30 text-muted-foreground border-transparent hover:border-muted-foreground/30'}`}
                                  onClick={async () => {
                                    const ids = assigned ? (room.assigned_provider_ids || []).filter(id => id !== prov.id) : [...(room.assigned_provider_ids || []), prov.id];
                                    await api.put(`/rooms/${room.id}`, { assigned_provider_ids: ids });
                                    fetchSettings();
                                  }}
                                  data-testid={`room-assign-${room.id}-${prov.id}`}
                                >{prov.name}</button>
                              );
                            })}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-xs text-red-500 h-7" onClick={async () => {
                          await api.delete(`/rooms/${room.id}`);
                          toast.success('Room deactivated');
                          fetchSettings();
                        }} data-testid={`room-deactivate-${room.id}`}>{room.is_active ? 'Deactivate' : 'Deactivated'}</Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Nurse Teams Configuration */}
          <TabsContent value="nurse_teams" className="space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Provider → Nurse Team Mapping</CardTitle>
                <CardDescription className="text-xs">Assign primary nurse and team to each provider for task routing and queue filtering</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {providers.map(prov => {
                  const map = nurseMaps.find(m => m.provider_id === prov.id) || {};
                  const currentNurseIds = map.nurse_ids || [];
                  const primaryNurseId = map.primary_nurse_id || '';
                  return (
                    <div key={prov.id} className="p-3 rounded-lg border space-y-2" data-testid={`nurse-map-${prov.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: prov.color }} />
                        <span className="text-sm font-semibold">{prov.name}</span>
                        <span className="text-[10px] text-muted-foreground">{prov.specialty}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {nurseUsers.map(nurse => {
                          const assigned = currentNurseIds.includes(nurse.id);
                          const isPrimary = primaryNurseId === nurse.id;
                          return (
                            <button key={nurse.id}
                              className={`text-[10px] px-2 py-1 rounded-lg border transition-all ${
                                isPrimary ? 'bg-primary text-primary-foreground border-primary shadow-sm' :
                                assigned ? 'bg-primary/10 text-primary border-primary/30' :
                                'bg-muted/30 text-muted-foreground border-transparent hover:border-slate-300'
                              }`}
                              onClick={async () => {
                                let newIds = [...currentNurseIds];
                                let newPrimary = primaryNurseId;
                                if (assigned) {
                                  newIds = newIds.filter(id => id !== nurse.id);
                                  if (newPrimary === nurse.id) newPrimary = newIds[0] || '';
                                } else {
                                  newIds.push(nurse.id);
                                  if (!newPrimary) newPrimary = nurse.id;
                                }
                                await api.put(`/provider-nurse-maps/${prov.id}`, { nurse_ids: newIds, primary_nurse_id: newPrimary });
                                fetchSettings();
                              }}
                              onDoubleClick={async () => {
                                // Double-click to set as primary
                                const newIds = currentNurseIds.includes(nurse.id) ? currentNurseIds : [...currentNurseIds, nurse.id];
                                await api.put(`/provider-nurse-maps/${prov.id}`, { nurse_ids: newIds, primary_nurse_id: nurse.id });
                                fetchSettings();
                              }}
                            >
                              {isPrimary ? '★ ' : ''}{nurse.name}
                            </button>
                          );
                        })}
                      </div>
                      {currentNurseIds.length > 0 && (
                        <p className="text-[9px] text-muted-foreground">
                          Primary: {nurseUsers.find(n => n.id === primaryNurseId)?.name || 'None'} · Team: {currentNurseIds.length}
                          <span className="ml-2 italic">Click to toggle, double-click to set primary</span>
                        </p>
                      )}
                    </div>
                  );
                })}
                {nurseUsers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No nurse users found. Create users with the "nurse" role first.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clinical Procedures Catalog */}
          <TabsContent value="procedures" className="space-y-4 m-0">
            <ClinicalProceduresCatalog />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}


function AiCredentialsCard() {
  const [status, setStatus] = useState(null);
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ aws_region: 'us-east-1', aws_access_key_id: '', aws_secret_access_key: '', deepgram_api_key: '' });

  useEffect(() => {
    Promise.all([
      api.get('/ai-config/status'),
      api.get('/ai-config/credentials'),
    ]).then(([s, c]) => {
      setStatus(s.data);
      setCreds(c.data);
      setForm(f => ({ ...f, aws_region: c.data.aws_region || 'us-east-1' }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { clinic_id: 'default', aws_region: form.aws_region };
      if (form.aws_access_key_id) payload.aws_access_key_id = form.aws_access_key_id;
      if (form.aws_secret_access_key) payload.aws_secret_access_key = form.aws_secret_access_key;
      if (form.deepgram_api_key) payload.deepgram_api_key = form.deepgram_api_key;
      await api.put('/ai-config/credentials', payload);
      toast.success('AI credentials saved');
      const [s, c] = await Promise.all([api.get('/ai-config/status'), api.get('/ai-config/credentials')]);
      setStatus(s.data); setCreds(c.data);
      setForm({ aws_region: 'us-east-1', aws_access_key_id: '', aws_secret_access_key: '', deepgram_api_key: '' });
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTest = async (task) => {
    setTesting(task);
    try {
      const res = await api.post('/ai-config/test', { task });
      toast.success(`${res.data.model}: "${res.data.response?.substring(0, 60)}..."`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTesting(null); }
  };

  if (loading) return <Card><CardContent className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></CardContent></Card>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><Key className="w-4 h-4" /> AI Service Credentials</CardTitle>
            <CardDescription className="text-xs">Amazon Bedrock + Deepgram — per-clinic configuration</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {status?.bedrock_configured ? (
              <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200" variant="outline"><CheckCircle2 className="w-3 h-3 mr-0.5" />Bedrock</Badge>
            ) : (
              <Badge className="text-[9px] bg-slate-50 text-slate-400 border-slate-200" variant="outline"><XCircle className="w-3 h-3 mr-0.5" />Bedrock</Badge>
            )}
            {status?.deepgram_configured ? (
              <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200" variant="outline"><CheckCircle2 className="w-3 h-3 mr-0.5" />Deepgram</Badge>
            ) : (
              <Badge className="text-[9px] bg-slate-50 text-slate-400 border-slate-200" variant="outline"><XCircle className="w-3 h-3 mr-0.5" />Deepgram</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current state */}
        {creds && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs space-y-1">
            <p className="text-slate-500">Source: <span className="font-medium text-slate-700">{creds.source}</span></p>
            {creds.aws_access_key_id && <p className="text-slate-500">AWS Key: <span className="font-mono text-slate-700">{creds.aws_access_key_id}</span></p>}
            {creds.deepgram_api_key && <p className="text-slate-500">Deepgram: <span className="font-mono text-slate-700">{creds.deepgram_api_key}</span></p>}
          </div>
        )}

        {/* Update form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">AWS Region</Label>
            <Input value={form.aws_region} onChange={e => setForm(f => ({ ...f, aws_region: e.target.value }))}
              className="mt-1 text-xs" placeholder="us-east-1" />
          </div>
          <div>
            <Label className="text-xs">AWS Access Key ID</Label>
            <Input value={form.aws_access_key_id} onChange={e => setForm(f => ({ ...f, aws_access_key_id: e.target.value }))}
              className="mt-1 text-xs font-mono" placeholder="AKIA..." />
          </div>
          <div>
            <Label className="text-xs">AWS Secret Access Key</Label>
            <Input type="password" value={form.aws_secret_access_key}
              onChange={e => setForm(f => ({ ...f, aws_secret_access_key: e.target.value }))}
              className="mt-1 text-xs font-mono" placeholder="Enter to update..." />
          </div>
          <div>
            <Label className="text-xs">Deepgram API Key</Label>
            <Input type="password" value={form.deepgram_api_key}
              onChange={e => setForm(f => ({ ...f, deepgram_api_key: e.target.value }))}
              className="mt-1 text-xs font-mono" placeholder="Enter to update..." />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs gap-1">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Credentials
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleTest('simple_summary')}
            disabled={!!testing} className="text-xs gap-1">
            {testing === 'simple_summary' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Test Nova Lite
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleTest('lab_summary')}
            disabled={!!testing} className="text-xs gap-1">
            {testing === 'lab_summary' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Test Nova Pro
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleTest('soap_note')}
            disabled={!!testing} className="text-xs gap-1">
            {testing === 'soap_note' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />} Test Claude
          </Button>
        </div>

        {/* Model routing table */}
        {status?.task_routing && (
          <div className="mt-3">
            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Task → Model Routing</p>
            <div className="grid grid-cols-2 gap-1 text-[10px]">
              {Object.entries(status.task_routing).map(([task, model]) => (
                <div key={task} className="flex items-center justify-between px-2 py-1 rounded bg-slate-50">
                  <span className="text-slate-600">{task.replace(/_/g, ' ')}</span>
                  <Badge variant="outline" className={`text-[8px] ${
                    model.includes('claude') ? 'border-violet-200 text-violet-600' :
                    model.includes('pro') ? 'border-blue-200 text-blue-600' :
                    'border-slate-200 text-slate-500'
                  }`}>{model}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
